import { useNavigate } from "react-router-dom";
import {
    ChartLineUp,
    MagnifyingGlass,
    Lightning,
    Target,
    ArrowRight,
    ShieldCheck,
    FileText,
    Sparkle,
} from "@phosphor-icons/react";

const Feature = ({ icon: Icon, title, body }) => (
    <div className="border border-neutral-300 bg-white p-5 flex flex-col gap-2">
        <Icon size={22} weight="bold" className="text-klein" />
        <h3 className="font-heading font-black text-lg uppercase tracking-tight text-ink">
            {title}
        </h3>
        <p className="text-sm text-neutral-700 leading-relaxed">{body}</p>
    </div>
);

const PublicFooter = () => (
    <footer className="border-t-2 border-ink bg-neutral-50 mt-24">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-6">
            <div>
                <div className="flex items-center gap-2">
                    <Lightning size={16} weight="bold" className="text-klein" />
                    <span className="font-heading font-black uppercase tracking-tight text-sm text-ink">
                        SerpScout
                    </span>
                </div>
                <p className="text-xs text-neutral-600 mt-1">
                    A Google Ads campaign management tool by Metro Berry Digital
                    Marketing Services.
                </p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-2">
                    © {new Date().getFullYear()} Metro Berry Digital Marketing
                    Services · All rights reserved
                </p>
            </div>
            <nav className="flex items-center gap-6 text-sm">
                <a
                    href="/privacy-policy"
                    className="text-ink hover:text-klein underline underline-offset-4"
                    data-testid="footer-privacy-link"
                >
                    Privacy Policy
                </a>
                <a
                    href="/terms-of-service"
                    className="text-ink hover:text-klein underline underline-offset-4"
                    data-testid="footer-terms-link"
                >
                    Terms of Service
                </a>
                <a
                    href="mailto:support@metroberrygoogleadsbuilder.com"
                    className="text-ink hover:text-klein underline underline-offset-4"
                >
                    Contact
                </a>
            </nav>
        </div>
    </footer>
);

export default function Landing() {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen bg-white text-ink">
            {/* Nav */}
            <header className="border-b border-neutral-200">
                <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Lightning
                            size={20}
                            weight="bold"
                            className="text-klein"
                        />
                        <span
                            data-testid="brand-serpscout"
                            className="font-heading font-black uppercase tracking-tight text-base"
                        >
                            SerpScout
                        </span>
                        <span className="ml-2 hidden sm:inline text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                            by Metro Berry Digital Marketing
                        </span>
                    </div>
                    <button
                        data-testid="landing-signin-btn"
                        onClick={() => navigate("/login")}
                        className="border border-ink bg-ink text-white hover:bg-klein hover:border-klein font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1 transition-colors"
                    >
                        Sign in with Google
                        <ArrowRight size={11} weight="bold" />
                    </button>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-6xl mx-auto px-6 pt-20 pb-16">
                <div className="grid md:grid-cols-5 gap-10 items-center">
                    <div className="md:col-span-3 space-y-6">
                        <div className="inline-flex items-center gap-2 border border-klein bg-klein/5 px-2 py-1">
                            <Sparkle
                                size={11}
                                weight="bold"
                                className="text-klein"
                            />
                            <span className="font-mono uppercase tracking-widest text-[10px] font-bold text-klein">
                                Google Ads campaign management
                            </span>
                        </div>
                        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
                            Audit, build & optimize Google Ads campaigns —{" "}
                            <span className="text-klein">without the busywork.</span>
                        </h1>
                        <p className="text-base text-neutral-700 leading-relaxed max-w-xl">
                            SerpScout is an internal SERP and campaign management tool
                            built by <b>Metro Berry Digital Marketing Services</b>. It
                            connects to a Google Ads account (with your permission)
                            so our team can build campaigns, audit search terms,
                            manage negative keywords, apply demographic bid adjustments,
                            and export everything for Google Ads Editor — all from one
                            place.
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                data-testid="landing-cta-primary"
                                onClick={() => navigate("/login")}
                                className="border border-klein bg-klein text-white hover:bg-ink hover:border-ink font-mono uppercase tracking-widest text-[10px] font-bold px-4 h-11 flex items-center gap-2 transition-colors"
                            >
                                Sign in with Google
                                <ArrowRight size={12} weight="bold" />
                            </button>
                            <a
                                href="#what-it-does"
                                className="border border-neutral-400 bg-white text-ink hover:border-ink font-mono uppercase tracking-widest text-[10px] font-bold px-4 h-11 flex items-center gap-2 transition-colors"
                            >
                                What it does
                            </a>
                        </div>
                        <div className="pt-4 flex items-center gap-2 text-xs text-neutral-500">
                            <ShieldCheck
                                size={14}
                                weight="bold"
                                className="text-emerald-600"
                            />
                            Google account data is used only for Ads management —
                            never sold, shared, or used to train ML models.
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <div className="border-2 border-ink bg-ink text-white p-6 space-y-4">
                            <div className="font-mono uppercase tracking-widest text-[10px] text-neutral-400">
                                What SerpScout accesses
                            </div>
                            <ul className="space-y-3 text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="text-klein">·</span> Read
                                    campaign, ad group, keyword and ad structure
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-klein">·</span> Pull
                                    performance metrics (impressions, clicks,
                                    cost, conversions)
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-klein">·</span> Create
                                    campaigns, ad groups, keywords and ads based
                                    on your inputs
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-klein">·</span> Apply
                                    bid modifiers & demographic exclusions you
                                    approve
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-klein">·</span> Export
                                    to Google Ads Editor–compatible CSV
                                </li>
                            </ul>
                            <div className="pt-2 border-t border-neutral-700 font-mono uppercase tracking-widest text-[9px] text-neutral-400">
                                Scope requested
                            </div>
                            <code className="block text-[11px] font-mono text-sky-300 break-all">
                                https://www.googleapis.com/auth/adwords
                            </code>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section
                id="what-it-does"
                className="max-w-6xl mx-auto px-6 pb-20"
            >
                <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-8">
                    What SerpScout does
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Feature
                        icon={MagnifyingGlass}
                        title="SERP Research"
                        body="Live Google Search results with paid + organic breakdown across mobile and desktop, per city."
                    />
                    <Feature
                        icon={Target}
                        title="Campaign Builder"
                        body="Build campaigns, ad groups, keywords and RSAs; push live to Google Ads or export for Google Ads Editor."
                    />
                    <Feature
                        icon={ChartLineUp}
                        title="Demographics & Bids"
                        body="Audit age, gender & income performance; apply per-campaign bid adjustments or exclusions in one click."
                    />
                    <Feature
                        icon={FileText}
                        title="Search Terms Audit"
                        body="Find wasted spend, flag AI-junk search terms, sync negatives back to your Google Ads account."
                    />
                </div>
            </section>

            {/* Trust block */}
            <section className="bg-neutral-50 border-y border-neutral-200">
                <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-10">
                    <div>
                        <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-3">
                            How your data is handled
                        </h2>
                        <p className="text-sm text-neutral-700 leading-relaxed">
                            SerpScout accesses Google Ads data only for the
                            campaign-management features you use inside the app.
                            No data obtained through the{" "}
                            <code className="text-xs bg-white border px-1">
                                adwords
                            </code>{" "}
                            scope is sold to third parties, shared with advertisers,
                            or used to train machine learning models. Access is
                            revocable at any time by removing SerpScout from your{" "}
                            <a
                                href="https://myaccount.google.com/permissions"
                                target="_blank"
                                rel="noreferrer"
                                className="underline underline-offset-4 text-klein"
                            >
                                Google account permissions
                            </a>
                            . Full details in our{" "}
                            <a
                                href="/privacy-policy"
                                className="underline underline-offset-4 text-klein"
                            >
                                Privacy Policy
                            </a>
                            .
                        </p>
                    </div>
                    <div>
                        <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-3">
                            Who's behind it
                        </h2>
                        <p className="text-sm text-neutral-700 leading-relaxed">
                            SerpScout is built and operated by{" "}
                            <b>Metro Berry Digital Marketing Services</b> — a
                            Google Ads agency that uses SerpScout internally for
                            its own client campaigns. If you're on our team,
                            sign in with your Google account above. For general
                            questions, email{" "}
                            <a
                                href="mailto:support@metroberrygoogleadsbuilder.com"
                                className="underline underline-offset-4 text-klein"
                            >
                                support@metroberrygoogleadsbuilder.com
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}
